import AiWriteButton from "../../components/AiWriteButton";
import React, { useState, useEffect } from "react";
import { T } from "../Translate";
import { isValidPhone, isValidEmail } from "../../utils/contactLinks";
import { useNavigate } from "react-router-dom";
import {
  Container, AppBar, Toolbar, Typography, TextField, Button, IconButton,
  Select, MenuItem, CircularProgress, Box, Snackbar, Alert, InputAdornment,
  FormControl, FormHelperText
} from "@mui/material";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../../icons";
import { VideoLibrary as VideoLibraryIcon } from "../../icons";
import { Close as CloseIcon } from "../../icons";
import { auth, db, uploadImage, uploadVideo } from "../../firebase";
import { doc, getDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import Atras from "../BackButton";
import { useSubscriptionContext } from "../SubscriptionContext";
import { getDatabase, ref, onValue } from "firebase/database";
import { barColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";
import LocationAutocompleteField from "../../components/LocationAutocompleteField";
import { uploadButtonSx, uploadProgressSx, removeMediaButtonSx, removeMediaIconSx } from "../mediaFormStyles";
import SortableImageGrid from "../../components/SortableImageGrid";



const FORM_VALIDATION = {
  whatsapp: {
    validate: (value) =>
      value && !isValidPhone(value) ? <T>Ingresá un número válido con código de país (ej: +5491122334455)</T> : "",
  },
  paypalEmail: {
    validate: (value) => (value && !isValidEmail(value) ? <T>Ingresá un correo de PayPal válido</T> : ""),
  },
  title: {
    required: true, minLength: 5, maxLength: 100,
    validate: (value) => {
      if (!value) return <T>El título es obligatorio</T>;
      if (value.length < 5) return <T>El título debe tener al menos 5 caracteres</T>;
      if (value.length > 100) return <T>El título no puede exceder 100 caracteres</T>;
      return null; // Return null instead of empty string for no error
    },
  },
  description: {
    required: true, minLength: 20, maxLength: 1000,
    validate: (value) => {
      if (!value) return <T>La descripción es obligatoria</T>;
      if (value.length < 20) return <T>La descripción debe tener al menos 20 caracteres</T>;
      return null;
    },
  },
  price: {
    required: true,
    validate: (value) => {
      if (!value) return <T>El precio es obligatorio</T>;
      const price = parseFloat(value);
      if (isNaN(price)) return <T>Ingrese un precio válido</T>;
      if (price <= 0) return <T>El precio debe ser mayor a 0</T>;
      return null;
    },
  },
  place: {
    required: true,
    validate: (value) => !value ? <T>El lugar es obligatorio</T> : null,
  },
  category: {
    required: true,
    validate: (value) => !value ? <T>La categoría es obligatoria</T> : null,
  },
  address: {
    required: true,
    validate: (value) => {
      if (!value || !value.trim()) return <T>La dirección es obligatoria</T>;
      if (value.trim().length < 3) return <T>La dirección es demasiado corta</T>;
      return null;
    },
  },
  images: {
    required: true,
    validate: (value) => {
      if (!Array.isArray(value) || value.length === 0) return <T>Debe subir al menos una imagen</T>;
      if (value.length > 15) return <T>No puede subir más de 15 imágenes</T>;
      return null;
    },
  },
  youtubeUrl: {
    required: false,
    validate: (value) => {
      if (!value) return null;
      const isValid = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{11}/.test(value.trim());
      return isValid ? null : <T>Ingresá un link de YouTube válido</T>;
    },
  },
};

const Publish = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    place: "",
    category: "",
    // "Dirección" con sugerencias; `city` se completa con la ciudad de la
    // sugerencia elegida (la usan los filtros y el orden por ciudad de trips.js).
    address: "",
    city: "",
    timeToDo: new Date(),
    images: [],
    whatsapp: "",
    paypalEmail: "",
    video: null,
    youtubeUrl: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const navigate = useNavigate();
  const { subscription, isSubscriptionActive } = useSubscriptionContext(); // Correct usage

  useEffect(() => {
    if (!subscription) {
      navigate('/subscription');
    }
  }, [subscription, navigate]);

  const PLAN_LIMITS_TRIPS = { Free: 1, Básico: 1, Profesional: 3, Premium: Infinity };
  const [tripCount, setTripCount] = useState(0);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !subscription) return;
    const rtdb = getDatabase();
    const now = new Date();
    const monthYear = `${now.getMonth() + 1}-${now.getFullYear()}`;
    const countsRef = ref(rtdb, `publicationCounts/${user.uid}/${monthYear}`);
    const unsubscribe = onValue(countsRef, (snap) => {
      setTripCount(snap.exists() ? snap.val().trips || 0 : 0);
    });
    return () => unsubscribe();
  }, [subscription]);

  const tripLimit = PLAN_LIMITS_TRIPS[subscription?.planName] ?? 0;
  const canPublishTrip = isSubscriptionActive && tripCount <= tripLimit;
  const tripLimitMsg = tripCount > tripLimit
    ? <T>Has superado el límite de {tripLimit} viaje{tripLimit !== 1 ? "s" : ""} por mes de tu plan {subscription?.planName}. Mejora tu plan para publicar más.</T>
    : "";

  const handleAddressChange = (address) => {
    setFormData((prev) => ({ ...prev, address }));
    setFormErrors((prev) => ({ ...prev, address: validateField("address", address) }));
  };

  const handleAddressSelect = (opt) => {
    setFormData((prev) => ({
      ...prev,
      city: opt.city || prev.city,
      latitude: opt.latitude,
      longitude: opt.longitude,
    }));
  };

  const validateField = (name, value) => {
    const fieldValidation = FORM_VALIDATION[name];
    return fieldValidation?.validate(value); // Use optional chaining
  };
  
  const validateForm = () => {
    const errors = {};
    for (const fieldName in FORM_VALIDATION) {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    }
    return errors; // Return the errors object directly
  };



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate field on change
    const error = validateField(name, value);
    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handlePublishProyect = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({ open: true, message: <T>Por favor, corrija los errores en el formulario.</T>, severity: "error" });
      return;
    }

    try {
      setLoading(true);
      const { uid } = auth.currentUser || {};
      if (!uid) throw new Error("Usuario no autenticado");

      const userDocRef = doc(db, "users", uid);
      const userDocSnapshot = await getDoc(userDocRef);

      if (!userDocSnapshot.exists()) {
        throw new Error("No se encontró el documento del usuario");
      }

      const userData = userDocSnapshot.data();
      await addDoc(collection(db, "places"), {
        name: userData.displayName,
        ...formData,
        whatsapp: formData.whatsapp.trim(),
        // Solo los alojamientos se reservan con seña por PayPal.
        paypalEmail: formData.category === "alojamiento" ? formData.paypalEmail.trim() : "",
        address: formData.address.trim(),
        city: formData.city || formData.address.trim(),
        createdAt: serverTimestamp(),
        uid,
      });

      setPublishSuccess(true);
      setSnackbar({
        open: true,
        message: <T>Propiedad publicada exitosamente</T>,
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: <T>Error al publicar:</T> + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;

    if (!files) return;

    // Validar límite de imágenes
    if (formData.images.length + files.length > 10) {
      setFormErrors((prev) => ({
        ...prev,
        images: <T>No puede subir más de 10 imágenes</T>,
      }));
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];
      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...imageUrls],
      }));

      // Limpiar error si hay imágenes
      setFormErrors((prev) => ({
        ...prev,
        images: "",
      }));
    } catch (error) {
      setFormErrors((prev) => ({
        ...prev,
        images: <T>Error al subir las imágenes</T>,
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      images: updatedImages,
    }));

    // Validar si no quedan imágenes
    if (updatedImages.length === 0) {
      setFormErrors((prev) => ({
        ...prev,
        images: <T>Debe subir al menos una imagen</T>,
      }));
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const videoUrl = await uploadVideo(file);
      setFormData((prev) => ({ ...prev, video: videoUrl }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: <T>Error al subir el video</T>,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVideo = () => {
    setFormData((prev) => ({ ...prev, video: null }));
  };

  const handleRedirectHome = () => {
    navigate("/home");
  };

  const handlePublishClick = async () => {
    await handlePublishProyect();
  };


  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar position="fixed" sx={{ bgcolor: barColors.background, color: barColors.text }}>
        <Toolbar>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
              maxWidth: { xs: "100%", md: "980px" },
              mx: "auto",
              px: 2,
              boxSizing: "border-box",
            }}
          >
            <Atras color="inherit" />
            <Typography variant="h5" noWrap>
              <T>Publicar Propiedad</T>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 14, mb: 20 }}>
        <TextField
          autoFocus
          margin="dense"
          id="title"
          label={<T>Título</T>}
          type="text"
          fullWidth
          name="title"
          required
          error={!!formErrors.title}
          helperText={formErrors.title}
          value={formData.title}
          onChange={handleInputChange}
        />
        <TextField
          margin="dense"
          id="description"
          label={<T>Describe la propiedad</T>}
          type="text"
          fullWidth
          name="description"
          multiline
          required
          error={!!formErrors.description}
          helperText={formErrors.description}
          rows={5}
          value={formData.description}
          onChange={handleInputChange}
        />
        <AiWriteButton
          type="place"
          fields={{
            titulo: formData.title,
            descripcion: formData.description,
            lugar: formData.place,
            tipo: formData.category,
            ciudad: formData.city,
            precio: formData.price && `USD ${formData.price}`,
          }}
          onResult={({ title, description }) =>
            setFormData((prev) => ({ ...prev, title: title || prev.title, description: description || prev.description }))
          }
        />
        <TextField
          margin="dense"
          id="price"
          label={<T>Precio en USD</T>}
          type="text"
          fullWidth
          required
          name="price"
          value={formData.price}
          onChange={(e) => {
            const value = e.target.value;

            // Solo permite números y un punto decimal
            if (/^\d*\.?\d*$/.test(value)) {
              // Si hay punto decimal, limita a 2 decimales
              if (value.includes(".")) {
                const [whole, decimal] = value.split(".");
                const formattedValue = `${whole}.${decimal.slice(0, 2)}`;
                setFormData((prev) => ({
                  ...prev,
                  price: formattedValue,
                }));
              } else {
                setFormData((prev) => ({
                  ...prev,
                  price: value,
                }));
              }
            }
          }}
          onBlur={(e) => {
            // Al perder el foco, asegura formato con 2 decimales
            const value = e.target.value;
            if (value) {
              const numberValue = parseFloat(value);
              if (!isNaN(numberValue)) {
                setFormData((prev) => ({
                  ...prev,
                  price: numberValue.toFixed(2),
                }));
              }
            }
          }}
          error={!!formErrors.price}
          helperText={formErrors.price}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <TextField
          margin="dense"
          name="whatsapp"
          label={<T>WhatsApp de contacto (opcional)</T>}
          type="tel"
          fullWidth
          variant="outlined"
          value={formData.whatsapp || ""}
          onChange={handleInputChange}
          error={!!formErrors.whatsapp}
          helperText={formErrors.whatsapp || <T>Con código de país, ej: +5491122334455. Se muestra en la publicación con un botón de WhatsApp.</T>}
        />
        {formData.category === "alojamiento" && (
          <TextField
            margin="dense"
            name="paypalEmail"
            label={<T>Correo de PayPal para recibir reservas (opcional)</T>}
            type="email"
            fullWidth
            variant="outlined"
            value={formData.paypalEmail || ""}
            onChange={handleInputChange}
            error={!!formErrors.paypalEmail}
            helperText={formErrors.paypalEmail || <T>Si lo completás, los huéspedes pueden reservar con una seña del 30% que va directo a tu PayPal.</T>}
          />
        )}
        <Typography sx={{ mt: 2 }}><T>Tipo de publicación:</T></Typography>
        <FormControl fullWidth required error={!!formErrors.category} sx={{ mt: 1.5, marginBottom: 1 }}>
          <Select
            label={<T>Categoría</T>}
            placeholder="Selecciona una categoría"
            type="text"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <T>Selecciona una categoría</T>
            </MenuItem>
            <MenuItem value="alojamiento"><T>Alojamiento</T></MenuItem>
            <MenuItem value="compra"><T>Compra</T></MenuItem>
            <MenuItem value="alquiler"><T>Alquiler</T></MenuItem>
            <MenuItem value="combo"><T>Combo Vuelo + Alojamiento</T></MenuItem>
          </Select>
          {formErrors.category && <FormHelperText>{formErrors.category}</FormHelperText>}
        </FormControl>
        <Typography sx={{ mt: 2 }}><T>Seleccione un lugar:</T></Typography>
        <FormControl fullWidth required error={!!formErrors.place} sx={{ mt: 1.5, marginBottom: 1 }}>
          <Select
            label={<T>Lugares</T>}
            placeholder="Selecciona un lugar"
            type="text"
            name="place"
            value={formData.place}
            onChange={handleInputChange}
            displayEmpty
          >
          <MenuItem value="" disabled>
            <T>Selecciona un continente</T>
          </MenuItem>
          <MenuItem value="África"><T>África</T></MenuItem>
          <MenuItem value="Asia"><T>Asia</T></MenuItem>
          <MenuItem value="América del Norte"><T>América del Norte</T></MenuItem>
          <MenuItem value="América del Sur"><T>América del Sur</T></MenuItem>
          <MenuItem value="Europa"><T>Europa</T></MenuItem>
          <MenuItem value="Medio Oriente"><T>Medio Oriente</T></MenuItem>
          <MenuItem value="Oceanía"><T>Oceanía</T></MenuItem>
          <MenuItem value="Antártida"><T>Antártida</T></MenuItem>
          </Select>
          {formErrors.place && <FormHelperText>{formErrors.place}</FormHelperText>}
        </FormControl>
        <LocationAutocompleteField
          mode="address"
          margin="dense"
          id="address"
          label={<T>Dirección</T>}
          value={formData.address}
          onChange={handleAddressChange}
          onSelect={handleAddressSelect}
          required
          error={!!formErrors.address}
          helperText={formErrors.address}
        />
        {/* Alert para mostrar estado de las imágenes */}
        {(formErrors.images || formData.images.length === 0) && (
          <Alert
            severity={formErrors.images ? "error" : "info"}
            sx={{ mt: 2, mb: 1 }}
          >
            {formErrors.images ||
              <T>Debe subir al menos una imagen de la propiedad</T>}
          </Alert>
        )}

        <label htmlFor="upload-button" style={{ width: "100%" }}>
          <Button
            variant="contained"
            fullWidth
            component="div"
            sx={uploadButtonSx(!!formErrors.images)}
            disabled={loading || formData.images.length >= 10}
          >
            <AddPhotoAlternateIcon />
            {formData.images.length === 0
              ? <T>Subir Imagen del producto</T>
              : <><T>Imágenes:</T> {formData.images.length}/15</>}
            {loading && (
              <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
            )}
          </Button>
        </label>

        <input
          type="file"
          accept="image/*"
          onClick={(event) => (event.target.value = null)}
          onChange={handleImageUpload}
          style={{ display: "none" }}
          id="upload-button"
          multiple
        />

        {/* Vista previa de las imágenes */}
        <SortableImageGrid
          images={formData.images}
          onChange={(images) => setFormData((prev) => ({ ...prev, images }))}
          onRemove={handleRemoveImage}
        />

        <Typography sx={{ mt: 3 }}><T>Video (opcional):</T></Typography>
        {formData.video ? (
          <Box sx={{ position: "relative", mt: 1, maxWidth: 320 }}>
            <video src={formData.video} controls style={{ width: "100%", borderRadius: "10px" }} />
            <IconButton sx={removeMediaButtonSx} onClick={handleRemoveVideo}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
          </Box>
        ) : (
          <>
            <label htmlFor="upload-video-button" style={{ width: "100%" }}>
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <VideoLibraryIcon />
                <T>Subir video de la propiedad</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="video/*"
              onClick={(event) => (event.target.value = null)}
              onChange={handleVideoUpload}
              style={{ display: "none" }}
              id="upload-video-button"
            />
          </>
        )}

        <TextField
          margin="dense"
          id="youtubeUrl"
          label={<T>Link de YouTube (opcional)</T>}
          type="text"
          fullWidth
          name="youtubeUrl"
          placeholder="https://www.youtube.com/watch?v=..."
          value={formData.youtubeUrl}
          onChange={handleInputChange}
          error={!!formErrors.youtubeUrl}
          helperText={formErrors.youtubeUrl}
          sx={{ mt: 2 }}
        />

        {publishSuccess && (
          <Box sx={{ textAlign: "center" }}>
            <Typography><T>El anuncio se ha publicado correctamente.</T></Typography>
            <Button onClick={handleRedirectHome} color="primary">
              <T>Ir a Inicio</T>
            </Button>
          </Box>
        )}
        {!publishSuccess && (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
              <Typography variant="caption" color={tripCount >= tripLimit ? "error" : "text.secondary"}>
                <T>Viajes este mes:</T> {tripCount} / {tripLimit === Infinity ? "∞" : tripLimit}
              </Typography>
            </Box>
            {tripLimitMsg && (
              <Alert severity="warning" sx={{ mt: 1 }} action={
                <Button size="small" color="inherit" onClick={() => navigate("/subscription")}><T>Ver planes</T></Button>
              }>{tripLimitMsg}</Alert>
            )}
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              fullWidth
              onClick={handlePublishClick}
              color="primary"
              disabled={!canPublishTrip}
            >
              <T>Publicar</T>
            </Button>
          </>
        )}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Container>
  );
};

export default Publish;
